import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { PALETTE, fmtMoney, fmtYears, daysFromMoney } from "@/lib/chart-utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * DebtEvolutionLine — only renders if 2+ fiscal-year data points exist.
 * Dual series: dette (monetary) and dette (years of life per capita).
 */
export default function DebtEvolutionLine({
  multiYearPoints = [],
  populationActive,
  salaireJournalierMoyen,
  currency = "UM",
}) {
  const data = useMemo(() => {
    return (multiYearPoints || []).map((p) => {
      const debt = Number(p.public_debt) || 0;
      let years = null;
      if (populationActive && salaireJournalierMoyen) {
        const perHead = debt / populationActive;
        const days = daysFromMoney(perHead, salaireJournalierMoyen);
        years = days ? days / 365 : null;
      }
      return {
        fiscal_year: p.fiscal_year,
        public_debt: debt,
        years_per_capita: years,
      };
    });
  }, [multiYearPoints, populationActive, salaireJournalierMoyen]);

  if (data.length < 2) return null;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        className="rounded-md border border-border bg-card p-3 text-xs shadow-lg"
        data-testid="tooltip-line"
      >
        <div className="font-semibold">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="mt-1 flex justify-between gap-4">
            <span className="text-muted-foreground" style={{ color: p.color }}>
              {p.name}
            </span>
            <span className="font-mono">
              {p.dataKey === "public_debt"
                ? fmtMoney(p.value, currency)
                : fmtYears(p.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card data-testid="chart-line">
      <CardHeader>
        <CardTitle className="text-base">
          Évolution de la dette en vies hypothéquées
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64 w-full" data-testid="chart-line-container">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="fiscal_year"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="public_debt"
                name="Dette (monétaire)"
                stroke={PALETTE.red}
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              {populationActive && salaireJournalierMoyen && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="years_per_capita"
                  name="Années/tête (vitaliste)"
                  stroke={PALETTE.gold}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
